<script setup lang="ts">
import { Loader2 } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

withDefaults(
  defineProps<{
    open: boolean;
    deleting?: boolean;
    allowDelete?: boolean;
  }>(),
  { deleting: false, allowDelete: true },
);

defineEmits<{
  "update:open": [value: boolean];
  remove: [];
  delete: [];
}>();
</script>

<template>
  <AlertDialog :open="open" @update:open="$emit('update:open', $event)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete recode card</AlertDialogTitle>
        <AlertDialogDescription>
          <template v-if="allowDelete">
            You can remove this card from the current dashboard, or delete the recode permanently.
          </template>
          <template v-else>
            This is a programming recode: it can be removed from this dashboard, and deleted from the questionnaire only.
          </template>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-4 rounded-lg border p-3">
          <p class="text-xs text-muted-foreground">
            The card is removed from this dashboard only. The recode stays available for other dashboards.
          </p>
          <Button
            variant="outline"
            size="sm"
            class="shrink-0"
            @click="$emit('remove')">
            Remove from dashboard
          </Button>
        </div>
        <div v-if="allowDelete" class="flex items-center justify-between gap-4 rounded-lg border p-3">
          <p class="text-xs text-muted-foreground">
            The recode is permanently deleted from all dashboards and from the available cards. This cannot be undone.
          </p>
          <Button
            variant="destructive"
            size="sm"
            class="shrink-0"
            :disabled="deleting"
            @click="$emit('delete')">
            <Loader2 v-if="deleting" class="h-4 w-4 animate-spin" />
            Delete
          </Button>
        </div>
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
